import React, { useState, useEffect, useRef } from 'react';
import BookingSummary from './BookingSummary';
import { formatPhone } from '../../../../utils/formatUtils';
import { getBusinessClients } from '../../../../firebase/clients';
import CustomNotice from '../../shared/CustomNotice';

/**
 * Componente de apresentação para o formulário de dados do cliente
 * 
 * @param {Object} props - Propriedades do componente
 * @param {Array} props.selectedServices - Serviços selecionados
 * @param {Date} props.selectedDate - Data selecionada
 * @param {string} props.selectedTime - Horário selecionado
 * @param {Object} props.selectedProfessional - Profissional selecionado
 * @param {Object} props.errors - Erros de validação do formulário
 * @param {Object} props.register - Função de registro do React Hook Form
 * @param {Function} props.handleSubmit - Função para submeter o formulário
 * @param {Function} props.onBack - Função chamada ao voltar
 * @param {boolean} props.isLoading - Indica se está carregando
 * @param {string} props.primaryColor - Cor primária para destaque
 * @param {Object} props.NavigationButtons - Componente de botões de navegação
 * @param {boolean} props.isAdmin - Indica se está no modo admin
 * @param {string} props.salonId - ID do salão para busca de clientes
 * @param {Function} props.setValue - Função para definir valores no formulário
 * @param {Object} props.salonConfig - Configurações do salão
 */
const ClientFormView = ({
  selectedServices = [],
  selectedDate,
  selectedTime,
  selectedProfessional,
  errors = {},
  register,
  handleSubmit,
  onBack,
  isLoading = false,
  primaryColor = '#4F46E5',
  NavigationButtons,
  isAdmin = false,
  salonId = null,
  setValue,
  salonConfig = {}
}) => {
  // Estados para o autocomplete
  const [searchTerm, setSearchTerm] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const suggestionsRef = useRef(null);
  
  const customNotices = salonConfig?.customNotices || {};

  // Buscar sugestões de clientes quando o admin digita o nome
  useEffect(() => {
    const fetchSuggestions = async () => {
      if (!isAdmin || !salonId || searchTerm.length < 2) {
        setSuggestions([]);
        return;
      }

      setIsSearching(true);
      try {
        const clients = await getBusinessClients(salonId, {
          search: searchTerm,
          limit: 5,
          forceRefresh: true
        });
        
        setSuggestions(clients || []);
      } catch (error) {
        console.error('Erro ao buscar sugestões de clientes:', error);
        setSuggestions([]);
      } finally {
        setIsSearching(false);
      }
    };

    // Debounce para evitar muitas requisições
    const timeoutId = setTimeout(fetchSuggestions, 300);
    return () => clearTimeout(timeoutId);
  }, [searchTerm, isAdmin, salonId]);

  // Fechar sugestões quando clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Selecionar um cliente das sugestões
  const selectClient = (client) => {
    setValue('client.name', client.nome);
    setValue('client.phone', client.celular);
    if (client.email) {
      setValue('client.email', client.email);
    }
    setShowSuggestions(false);
  };

  // Handler para mudança no campo de nome
  const handleNameChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    if (value.length >= 2 && isAdmin) {
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-3/4 mb-4"></div>
          <div className="h-64 bg-gray-100 rounded-lg mb-4"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
      <h2 className="text-xl font-semibold mb-4">
        {isAdmin ? "Preencha os dados do cliente" : "Seus dados"}
      </h2>
      
      {/* Aviso personalizado geral */}
      {customNotices.generalNotice?.enabled && 
       (customNotices.generalNotice.displayLocation === 'all' || 
        customNotices.generalNotice.displayLocation === 'clientForm') && (
        <CustomNotice 
          message={customNotices.generalNotice.message} 
          variant={customNotices.generalNotice.variant || "gray"} 
          className="mb-4"
        />
      )}
      
      <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
        <div className="space-y-4 mb-6">
          {/* Nome com Autocomplete para admin */}
          <div className="relative">
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
              Nome
            </label>
            <input
              id="name"
              type="text"
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                errors.client?.name ? 'border-red-300 focus:ring-red-200' : 'border-gray-300 focus:ring-blue-100'
              }`}
              placeholder="Nome completo do cliente"
              {...register('client.name', {
                onChange: isAdmin ? handleNameChange : undefined
              })}
            />
            {errors.client?.name && (
              <p className="mt-1 text-sm text-red-600">{errors.client.name.message}</p>
            )}

            {/* Sugestões de autocomplete (apenas para admin) */}
            {isAdmin && showSuggestions && (
              <div 
                ref={suggestionsRef}
                className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto"
              >
                {isSearching ? (
                  <div className="p-3 text-sm text-gray-500">Buscando clientes...</div>
                ) : suggestions.length > 0 ? (
                  <ul>
                    {suggestions.map((client) => (
                      <li 
                        key={client.id}
                        className="p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                        onClick={() => selectClient(client)}
                      >
                        <div className="font-medium">{client.nome}</div>
                        <div className="text-sm text-gray-600 flex space-x-3">
                          <span>{client.celular}</span>
                          {client.email && <span>{client.email}</span>}
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : searchTerm.length >= 2 ? (
                  <div className="p-3 text-sm text-gray-500">Nenhum cliente encontrado</div>
                ) : null}
              </div>
            )}
          </div>
          
          {/* Telefone */}
          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
              Telefone
            </label>
            <input
              id="phone"
              type="tel"
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                errors.client?.phone ? 'border-red-300 focus:ring-red-200' : 'border-gray-300 focus:ring-blue-100'
              }`}
              placeholder="(00) 00000-0000"
              {...register('client.phone', {
                onChange: (e) => {
                  e.target.value = formatPhone(e.target.value);
                }
              })}
            />
            {errors.client?.phone && (
              <p className="mt-1 text-sm text-red-600">{errors.client.phone.message}</p>
            )}
          </div>
          
          {/* Email */}
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              Email (opcional)
            </label>
            <input
              id="email"
              type="email"
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                errors.client?.email ? 'border-red-300 focus:ring-red-200' : 'border-gray-300 focus:ring-blue-100'
              }`}
              placeholder={isAdmin ? "email@cliente.com" : "seu@email.com"}
              {...register('client.email')}
            />
            {errors.client?.email && (
              <p className="mt-1 text-sm text-red-600">{errors.client.email.message}</p>
            )}
          </div>
          
          {/* Observações */}
          <div>
            <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">
              Observações (opcional)
            </label>
            <textarea
              id="notes"
              rows="3"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-100"
              placeholder={isAdmin ? "Observações importantes sobre o agendamento" : "Alguma informação adicional importante?"}
              {...register('client.notes')}
            ></textarea>
          </div>
        </div>
        
        {/* Resumo do Agendamento */}
        <BookingSummary
          selectedServices={selectedServices}
          selectedDate={selectedDate}
          selectedTime={selectedTime}
          selectedProfessional={selectedProfessional}
        />
        
        {/* Botões de Navegação */}
        <div className="mt-6">
          <NavigationButtons
            onBack={onBack}
            onContinue={handleSubmit}
            continueDisabled={false}
            continueLabel={isAdmin ? "Finalizar Agendamento" : "Confirmar Agendamento"}
            showBack={true}
            primaryColor={primaryColor}
          />
        </div>
      </form>
    </div>
  );
};

export default ClientFormView; 